import React, { useEffect, useRef } from 'react';
import './styles.css'; // Import the prefixed CSS

// --- Import FontAwesomeIcon Component ---
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// --- Import ALL Necessary Icons from Font Awesome (Updated for Planning & Design Content) ---
import {
    // Core Concepts & General
    faHeartPulse,
    faDraftingCompass,  // Planning/Design
    faBuilding,         // Architecture/Campus
    faSitemap,          // Master Planning
    faLightbulb,        // Vision/Concept
    faCheckCircle,      // Compliance/Quality
    faUsers,            // Team/Collaboration
    faGlobeAsia,        // Geography/Regions
    faStar,             // Excellence/Why Choose Us
    faProjectDiagram,   // Process/Workflow
    faRulerCombined,    // Design/Space
    faMapMarkedAlt,     // Site Evaluation/Location
    faNetworkWired,     // Interdepartmental/Flow
    faTools,            // Development/Execution
    // Additional Services
    faTasks,            // Functional Programming
    faIndustry,         // Greenfield/Brownfield (Building icon can also work)
    faCogs,             // Medical Equipment Planning (can also use faStethoscope)
    faLeaf,             // Energy-Efficient/Sustainable
    faPalette,          // Interior Design
    // Experience/Credentials
    faAward,            // Experience/Certification
    // Client Types
    faHospital,         // Hospitals/Institutions
    faUserMd,           // Clinics/Specialists
    faUniversity,       // Medical Colleges
    faDiagnoses,        // Diagnostic Centres
    // Keep other potentially useful icons if needed
    // Icons likely unused from previous versions:
    /*
    faCalculator, faBalanceScale, faArrowTrendUp, faScissors, faMagnifyingGlassChart,
    faShieldAlt, faHandHoldingDollar, faClipboardCheck, faBinoculars, faBuildingShield,
    faRoad, faHeartPulse, faUsers, faGavel
    */
} from '@fortawesome/free-solid-svg-icons'; // Using free solid icons

// --- Intersection Observer Hooks (No changes needed in hook logic) ---
const useIntersectionObserverTitle = (options) => {
    const containerRef = useRef(null);
    const [isVisible, setIsVisible] = React.useState(false);
    const callbackFunction = (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) setIsVisible(true);
    };
    useEffect(() => {
        const observer = new IntersectionObserver(callbackFunction, options);
        const currentRef = containerRef.current;
        if (currentRef) observer.observe(currentRef);
        return () => { if (currentRef) observer.unobserve(currentRef); };
    }, [containerRef, options]);
    return [containerRef, isVisible];
};

const useIntersectionObserverElements = (options) => {
    useEffect(() => {
        const observerCallback = (entries, observer) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    const target = entry.target;
                    const parent = target.parentElement;
                    const siblings = parent ? Array.from(parent.children).filter(el => el.classList.contains('cliniqor-fade-in-up')) : [target];
                    const index = siblings.indexOf(target);
                    const staggerIndex = index >= 0 ? index : 0;

                    target.style.setProperty('--item-index', staggerIndex % 8); // Keep stagger logic
                    target.classList.add('cliniqor-visible'); // Use prefixed class
                    observer.unobserve(target);
                }
            });
        };
        const observer = new IntersectionObserver(observerCallback, options);
        const targets = document.querySelectorAll('.cliniqor-fade-in-up');
        targets.forEach(target => observer.observe(target));
        return () => targets.forEach(target => { if (target) observer.unobserve(target); });
    }, [options]);
};
// --- End Observer Hooks ---


function App() {

    // --- Observer Setup for Titles (Adjusted Refs) ---
    const titleObserverOptions = { threshold: 0.2 }; // Lower threshold slightly if titles are short
    const [title1Ref, title1Visible] = useIntersectionObserverTitle(titleObserverOptions); // What is Master Planning?
    const [title2Ref, title2Visible] = useIntersectionObserverTitle(titleObserverOptions); // Why Choose Cliniqor?
    const [title3Ref, title3Visible] = useIntersectionObserverTitle(titleObserverOptions); // Planning Process
    const [title4Ref, title4Visible] = useIntersectionObserverTitle(titleObserverOptions); // Additional Services
    const [title5Ref, title5Visible] = useIntersectionObserverTitle(titleObserverOptions); // Regions & Who We Serve (Optional Title)
    const [title6Ref, title6Visible] = useIntersectionObserverTitle(titleObserverOptions); // Why Cliniqor Stands Apart

    // --- Observer Setup for General Fade-In Elements ---
    useIntersectionObserverElements({ threshold: 0.1 });

    // Choose a relevant hero background image for planning/design
    const heroBgImage = 'https://images.unsplash.com/photo-1606744824163-985d376605aa?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1920&q=80'; // Architecture/blueprint image

  return(
    // Use prefixed base wrapper class
    <div className="cliniqor-app-wrapper">

      {/* --- HERO SECTION --- */}
      <header
        className="cliniqor-hero-section" // Prefixed
        style={{ backgroundImage: `url(${heroBgImage})` }}
      >
        <div className="cliniqor-container"> {/* Prefixed */}
          <div className="cliniqor-hero-content"> {/* Prefixed */}
            {/* --- NEW HERO CONTENT --- */}
            <h1>Healthcare Planning and Designing</h1>
            <p className="cliniqor-sub-heading">
              Master Planning & Campus Design by Cliniqor: Designing Healthcare Spaces That Inspire Healing, Enable Efficiency, and Ensure Future Readiness
            </p>
            <p className="cliniqor-intro-text">
              We craft sustainable, healing-centric environments balancing form, function, and future growth. Our approach combines medical science, operations, engineering, and architectural excellence for long-term clinical success and patient experience.
            </p>
            {/* --- END NEW HERO CONTENT --- */}
          </div>
        </div>
      </header>

      <main>
        {/* --- WHAT IS MASTER PLANNING? --- */}
        <section className="cliniqor-section cliniqor-section-first cliniqor-section-bg-gradient cliniqor-intro-section">
          <div className="cliniqor-container">
            <div ref={title1Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title1Visible ? 'cliniqor-visible' : ''}`}>
              <h2>What Is Healthcare Master Planning?</h2>
            </div>
             <div className="cliniqor-intro-grid"> {/* Keeping grid structure */}
                 <div className="cliniqor-intro-text-content cliniqor-fade-in-up">
                     {/* --- NEW INTRO CONTENT --- */}
                     <p>A strategic and phased framework for designing a hospital or medical campus, integrating vision alignment, site-specific planning, departmental flow optimization, and long-term scalability & sustainability.</p>
                     <p>It's the art of envisioning transformative healing spaces and the science of systematizing inter-departmental synergies and compliance with global healthcare standards.</p>
                     {/* Can add core components list here if desired */}
                     {/* <ul>
                         <li>Vision alignment with objectives</li>
                         <li>Site-specific infrastructure planning</li>
                         <li>Departmental adjacency & flow optimization</li>
                         <li>Long-term scalability & sustainability</li>
                     </ul> */}
                     {/* --- END NEW INTRO CONTENT --- */}
                 </div>
             </div>
          </div>
        </section>

        {/* --- WHY CHOOSE CLINIQOR? (Features Section) --- */}
        <section className="cliniqor-section cliniqor-features-section cliniqor-section-bg-light">
          <div className="cliniqor-container">
            <div ref={title2Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title2Visible ? 'cliniqor-visible' : ''}`}>
              {/* --- NEW SECTION TITLE --- */}
              <h2>Why Choose Cliniqor for Healthcare Planning & Designing?</h2>
              {/* <p>A multidisciplinary team dedicated to evidence-based, compliant, and future-ready design.</p> */}
              {/* --- END NEW SECTION TITLE --- */}
            </div>
            <div className="cliniqor-features-grid"> {/* Grid structure maintained */}
              {/* --- NEW FEATURE CARDS --- */}
              <div className="cliniqor-feature-card cliniqor-fade-in-up">
                <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faAward} /></div>
                    <h4>15+ Years Domain Experience</h4>
                    <p>Proven track record in specialized healthcare planning and design.</p>
                </div>
              </div>
              <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faUsers} /></div>
                    <h4>Certified Experts</h4>
                    <p>Team includes certified Healthcare Architects & Engineers.</p>
                </div>
              </div>
              <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faLightbulb} /></div> {/* Icon for Principles */}
                    <h4>Evidence-Based Design</h4>
                    <p>Applying research-backed principles for optimal healing environments.</p>
                </div>
              </div>
               <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faCheckCircle} /></div>
                    <h4>Compliance Expertise</h4>
                    <p>Adherence to  JCI, NBC, CPHEEO, AERB, MOHFW standards.</p>
                </div>
              </div>
              <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faGlobeAsia} /></div>
                    <h4>Global Reach</h4>
                    <p>Projects across India, Middle East, and South-East Asia.</p>
                </div>
              </div>
               <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faUsers} /></div> {/* Repeat users icon for team */}
                    <h4>Multi-disciplinary Team</h4>
                    <p>Strategists, planners, engineers, architects, MEP, digital experts.</p>
                </div>
              </div>
              {/* --- END NEW FEATURE CARDS --- */}
            </div>
          </div>
        </section>

        {/* --- PLANNING PROCESS (Detailed Planning Section) --- */}
        <section className="cliniqor-section cliniqor-section-bg-texture cliniqor-detailed-planning-section"> {/* Kept existing classes */}
          <div className="cliniqor-container">
             <div ref={title3Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title3Visible ? 'cliniqor-visible' : ''}`}>
              {/* --- NEW SECTION TITLE --- */}
              <h2>Our Comprehensive Healthcare Master Planning Process</h2>
               <p>A 4-Phase Integrated Planning Methodology ensuring a holistic approach.</p>
               {/* --- END NEW SECTION TITLE --- */}
            </div>
            {/* Using Planning Items for Phases - simplified content */}
            <div className="cliniqor-planning-items-wrapper">
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faLightbulb} /></div> {/* Vision */}
                    <div className="cliniqor-content"><h4>Phase I: Strategic Visioning & Business Assessment</h4> <p>Aligning vision, market/financial feasibility, medical program definition, pre-assessment.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faMapMarkedAlt} /></div> {/* Site */}
                    <div className="cliniqor-content"><h4>Phase II: Site Evaluation & Conceptual Planning</h4> <p>Land assessment, clearances, circulation analysis, initial concept, sustainability goals.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faNetworkWired} /></div> {/* Flow */}
                    <div className="cliniqor-content"><h4>Phase III: Interdepartmental Planning & Flow Design</h4> <p>Space programming, workflow/adjacency analysis, critical unit location, infection control, tech integration.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faTools} /></div> {/* Build */}
                    <div className="cliniqor-content"><h4>Phase IV: Physical Development, Evaluation & Rollout</h4> <p>Detailed design (Arch, Struct, MEP), BIM, phasing, monitoring, commissioning advisory.</p> </div>
                 </div>
                 {/* Reduced items to 4 */}
            </div>
          </div>
        </section>

        {/* --- ADDITIONAL SERVICES (New Section using Features Layout) --- */}
        <section className="cliniqor-section cliniqor-features-section cliniqor-section-bg-light">
           <div className="cliniqor-container">
              <div ref={title4Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title4Visible ? 'cliniqor-visible' : ''}`}>
                 <h2>Additional Healthcare Design Services by Cliniqor</h2>
                 <p>Specialized services to complement our master planning expertise.</p>
              </div>
              <div className="cliniqor-features-grid">
                    {/* --- Cards for Additional Services --- */}
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faTasks} /></div>
                            <h4>Functional & Space Programming</h4>
                            <p>Designing space programs based on workflow, volume, and requirements for productivity.</p>
                        </div>
                    </div>
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faIndustry} /></div>
                            <h4>Greenfield & Brownfield Planning</h4>
                            <p>Tailor-made master plans for new hospitals or upgrades to existing facilities.</p>
                        </div>
                    </div>
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faCogs} /></div>
                            <h4>Medical Equipment Planning</h4>
                            <p>Integrating equipment needs into spatial design for optimal utilities and flow.</p>
                        </div>
                    </div>
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faLeaf} /></div>
                            <h4>Energy-Efficient Hospital Design</h4>
                            <p>Integrating passive cooling, day-lighting, recycling, and renewables for sustainability.</p>
                        </div>
                    </div>
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faPalette} /></div>
                            <h4>Hospital Interior Design</h4>
                            <p>Crafting healing environments using evidence-based palettes, acoustics, lighting, and ergonomics.</p>
                        </div>
                    </div>
                    {/* Can add a 6th empty card or adjust grid styling if needed */}
               </div>
           </div>
        </section>

       {/* --- REGIONS & WHO WE SERVE (Combined Simple Section) --- */}
        <section className="cliniqor-section cliniqor-section-bg-dark cliniqor-simple-list-section"> {/* Reusing dark background */}
           <div className="cliniqor-container">
              {/* Optional Title */}
               {/* <div ref={title5Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title5Visible ? 'cliniqor-visible' : ''}`}>
                 <h2>Our Reach and Clientele</h2>
               </div> */}
              <div className="cliniqor-two-col-list cliniqor-fade-in-up">
                  <div className="cliniqor-list-col">
                      <h4 style={{ color: '#fff', marginBottom: '15px' }}> <FontAwesomeIcon icon={faGlobeAsia} style={{ marginRight: '10px' }}/>Cities and Regions We Serve</h4>
                      <ul className="cliniqor-simple-list">
                          <li>Delhi NCR, Mumbai, Pune, Bangalore</li>
                          <li>Chennai, Hyderabad, Ahmedabad, Guwahati</li>
                          <li>Kolkata, Kochi, Bhopal, Lucknow</li>
                          <li>Jaipur, Chandigarh, Indore, Patna</li>
                          <li>Nagpur, Raipur, Bhubaneswar</li>
                          <li>International: UAE, Qatar, Kenya, Nepal</li>
                      </ul>
                  </div>
                   <div className="cliniqor-list-col">
                      <h4 style={{ color: '#fff', marginBottom: '15px' }}> <FontAwesomeIcon icon={faHospital} style={{ marginRight: '10px' }}/>Who We Serve</h4>
                       <ul className="cliniqor-simple-list">
                          <li>Multispecialty Hospitals</li>
                          <li>Medical Colleges & Teaching Hospitals</li>
                          <li>Daycare Centres & Clinics</li>
                          <li>Tertiary & Quaternary Care Institutions</li>
                          <li>Diagnostic & Imaging Centres</li>
                          <li>Rehabilitation & Geriatric Facilities</li>
                          <li>Wellness Centres & AYUSH Hospitals</li>
                       </ul>
                  </div>
              </div>
           </div>
           {/* Basic CSS needed for cliniqor-two-col-list, cliniqor-list-col, cliniqor-simple-list */}
           <style>{`
               .cliniqor-simple-list-section .cliniqor-container { max-width: 1000px; }
               .cliniqor-two-col-list { display: flex; flex-wrap: wrap; justify-content: space-between; gap: 20px; }
               .cliniqor-list-col { flex: 1; min-width: 280px; }
               .cliniqor-simple-list { list-style: none; padding-left: 0; margin: 0; color: #eee; }
               .cliniqor-simple-list li { margin-bottom: 8px; font-size: 0.95em; }
               @media (max-width: 600px) {
                   .cliniqor-list-col { min-width: 100%; }
               }
           `}</style>
        </section>

        {/* --- WHY CLINIQOR STANDS APART --- */}
        <section className="cliniqor-section"> {/* Default background */}
           <div className="cliniqor-container">
              <div ref={title6Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title6Visible ? 'cliniqor-visible' : ''}`}>
                 <h2>Why Cliniqor Stands Apart</h2>
                 <p>Our unique differentiators ensure comprehensive support and exceptional results.</p>
              </div>
              {/* Using Planning Items again for consistency */}
              <div className="cliniqor-planning-items-wrapper">
                    <div className="cliniqor-planning-item cliniqor-fade-in-up">
                        <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faSitemap} /></div> {/* Representing 360 support */}
                        <div className="cliniqor-content"><h4>360° Hospital Planning Support</h4> <p>Comprehensive assistance from initial vision to final commissioning.</p> </div>
                    </div>
                    <div className="cliniqor-planning-item cliniqor-fade-in-up">
                        <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faCheckCircle} /></div>
                        <div className="cliniqor-content"><h4>Unmatched Compliance Record</h4> <p>Deep expertise ensuring adherence to all relevant healthcare standards.</p> </div>
                    </div>
                    <div className="cliniqor-planning-item cliniqor-fade-in-up">
                        <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faUsers} /></div>
                        <div className="cliniqor-content"><h4>Interdisciplinary Expert Network</h4> <p>Access to a wide range of specialists for holistic solutions.</p> </div>
                    </div>
                    <div className="cliniqor-planning-item cliniqor-fade-in-up">
                        <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faHeartPulse} /></div> {/* Representing patient-centered */}
                        <div className="cliniqor-content"><h4>Patient-Centered Design Philosophy</h4> <p>Creating spaces focused on healing, comfort, and positive experiences.</p> </div>
                    </div>
                    <div className="cliniqor-planning-item cliniqor-fade-in-up">
                        <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faTools} /></div> {/* Representing post-project support */}
                        <div className="cliniqor-content"><h4>Robust Post-Project Support</h4> <p>Ongoing advisory and support during execution and beyond.</p> </div>
                    </div>
              </div>
           </div>
        </section>


      </main>

      {/* --- FOOTER CTA --- */}
      <footer className="cliniqor-section cliniqor-section-narrow cliniqor-footer-cta">
        <div className="cliniqor-container cliniqor-fade-in-up">
          {/* --- UPDATED FOOTER CONTENT --- */}
          <h3>Design the Future of Healthcare with Cliniqor</h3>
          <p>Partner with us to create exceptional, efficient, and healing-centric healthcare environments. Let's build the future of care, together.</p>
          {/* --- END UPDATED FOOTER CONTENT --- */}
          <a href="#contact" className="cliniqor-cta-button">Request a Planning Consultation</a> {/* Button kept */}
        </div>
      </footer>
    </div> // End cliniqor-app-wrapper
  );
}

export default App;